class YgProductCollapse extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.button = this.querySelector(".yg-product-card-button");
    this.content = this.querySelector(".yg-product-more-content");

    if (!this.button || !this.content) return;

    // 初始状态
    this.content.style.height = "0px";
    this.content.style.overflow = "hidden";
    this.content.style.transition = "height 0.3s ease";

    this.button.addEventListener("click", () => {
      this.toggle();
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.classList.add("is-open");

    const paddingTop = 16;

    // 初始为 0，避免 height 计算污染
    this.content.style.paddingTop = "0px";

    const height = this.content.scrollHeight + paddingTop;

    // 同一帧内还原 padding + 设置高度
    this.content.style.paddingTop = paddingTop + "px";
    this.content.style.height = height + "px";

    this.content.addEventListener(
      "transitionend",
      () => {
        if (this.isOpen) {
          // 展开完成后释放高度
          this.content.style.height = "auto";
        }
      },
      { once: true }
    );
  }

  close() {
    this.isOpen = false;
    this.classList.remove("is-open");

    const paddingTop = 16;

    // 固定当前高度（含 padding）
    const height = this.content.scrollHeight + paddingTop;
    this.content.style.height = height + "px";

    requestAnimationFrame(() => {
      // 收起：高度 → 0，同时移除 padding
      this.content.style.height = "0px";
      this.content.style.paddingTop = "0px";
    });
  }
}

if (!customElements.get("yg-product-collapse")) {
  customElements.define("yg-product-collapse", YgProductCollapse);
}

class YGDownload extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.addEventListener("click", this.handleDownload.bind(this));
  }

  handleDownload(event) {
    event.preventDefault();

    const downloadLink = this.getAttribute("data-download-link");
    if (!downloadLink) {
      console.warn("YGDownload: No data-download-link attribute found");
      return;
    }

    // Try to fetch the resource as a blob and force a download. This works even for non-image types.
    (async () => {
      try {
        const response = await fetch(downloadLink, { mode: "cors" });
        if (!response.ok)
          throw new Error("Network response was not ok: " + response.status);

        const blob = await response.blob();

        // Try to get filename from Content-Disposition header if present
        let filename = this.getAttribute("data-download-filename") || "";
        const disposition =
          response.headers.get("Content-Disposition") ||
          response.headers.get("content-disposition");
        if (!filename && disposition) {
          const match = disposition.match(
            /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i
          );
          if (match && match[1]) {
            filename = decodeURIComponent(match[1]);
          }
        }

        if (!filename) {
          // fallback to URL last segment
          try {
            filename = new URL(downloadLink).pathname.split("/").pop() || "";
          } catch (e) {
            filename = downloadLink.split("/").pop().split("?")[0];
          }
        }

        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = blobUrl;
        // set download attribute; empty string lets browser pick name but some browsers require a value
        if (filename) a.download = filename;
        else a.download = "";
        document.body.appendChild(a);
        a.click();
        a.remove();
        // revoke after a while
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
        console.log(
          "YGDownload: blob download initiated for",
          downloadLink,
          "as",
          filename
        );
      } catch (err) {
        // Fallback: try using a normal anchor with download attribute (may be blocked for cross-origin)
        try {
          const link = document.createElement("a");
          link.href = downloadLink;
          link.download = this.getAttribute("data-download-filename") || "";
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.warn(
            "YGDownload: fallback download attempted for",
            downloadLink
          );
        } catch (e) {
          console.error("YGDownload: Failed to download file", e);
        }
      }
    })();
  }
}

if (!customElements.get("yg-download")) {
  customElements.define("yg-download", YGDownload);
}

class YGMegaMenuTab extends HTMLElement {
  constructor() {
    super();
    this.tabs = null;
    this.items = null;
    this.tabHandlers = [];
    this.handleResize = this.handleResize.bind(this);
    this.init();
  }
  init() {
    this.tabs = this.querySelectorAll(".yg-tab-item");
    this.items = this.querySelectorAll(".yg-content-item");
    if (this.tabs.length === 0 || this.items.length === 0) return;

    // 初始化高亮和展示
    this.tabs.forEach((tab, i) => {
      tab.classList.toggle("active", i === 0);
      if (this.items[i]) this.items[i].classList.toggle("active", i === 0);
    });

    // 根据屏幕宽度设置事件
    this.handleResize();
    window.addEventListener("resize", this.handleResize);

  }

  switchTab(index) {
    this.tabs.forEach((t, j) => t.classList.toggle("active", index === j));
    this.items.forEach((p, j) => p.classList.toggle("active", index === j));
  }

  handleResize() {
    const isMobile = window.innerWidth < 1150;

    // 清除现有事件监听器
    this.tabHandlers.forEach(({ tab, handler, type }) => {
      tab.removeEventListener(type, handler);
    });
    this.tabHandlers = [];

    // 根据设备类型添加事件
    this.tabs.forEach((tab, i) => {
      let handler;
      let type;

      if (isMobile) {
        // 移动端：点击切换
        type = "click";
        handler = () => this.switchTab(i);
      } else {
        // 桌面端：悬浮切换
        type = "mouseenter";
        handler = () => this.switchTab(i);
      }

      tab.addEventListener(type, handler);
      this.tabHandlers.push({ tab, handler, type });
    });
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    // 清除所有事件监听器
    this.tabHandlers.forEach(({ tab, handler, type }) => {
      tab.removeEventListener(type, handler);
    });
  }
}

if (!customElements.get("yg-mega-menu-tab")) {
  customElements.define("yg-mega-menu-tab", YGMegaMenuTab);
}

class YGItemProducts extends HTMLElement {
  constructor() {
    super();
    this.init();
  }
  init() {
    const products = this.querySelector(".yg-item-products");
    
    const panelWrapper = this.closest(".panel__wrapper");
    panelWrapper.removeAttribute('hidden');
    const contentHeight = products.offsetHeight;
    const scrollHeight = products.scrollHeight;
    panelWrapper.setAttribute('hidden','');
    if (contentHeight < scrollHeight) {
      
      const moreWrapper = this.querySelector(".yg-more-content");
      moreWrapper.classList.remove("hide");
      const moreButton = this.querySelector(".yg-more-content .yg-button-outline");
      const dropContainer = this.closest(".yg-dropdown-content-container");
      moreButton.addEventListener("click", () => {
        products.classList.add("active");
        moreWrapper.classList.add("hide");
        dropContainer.style.setProperty("--height", products.scrollHeight + "px");
      });
    }
    
    
  }
}

if (!customElements.get("yg-item-products")) {
  customElements.define("yg-item-products", YGItemProducts);
}

class YGBuyNow extends HTMLElement {
  constructor() {
    super();
    this.init();
  }
  init() {
    this.addEventListener("click", this.handleClick.bind(this));
  }
  handleClick(event) {
    event.preventDefault();
    if (this.querySelector('.shopify-payment-button__more-options')) {
      this.querySelector('.shopify-payment-button__more-options').click();
    } else if(this.querySelector('.shopify-payment-button__button')) {
      this.querySelector('.shopify-payment-button__button').click();
    }
  }
}
if (!customElements.get("yg-buy-now")) {
  customElements.define("yg-buy-now", YGBuyNow);
}

const CALENDLY_WIDGET_SCRIPT_URL =
  "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_WIDGET_STYLE_URL =
  "https://assets.calendly.com/assets/external/widget.css";

let calendlyWidgetPromise = null;

function ensureCalendlyWidgetStyles() {
  if (document.querySelector('link[data-calendly-widget-style="true"]')) return;

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = CALENDLY_WIDGET_STYLE_URL;
  link.dataset.calendlyWidgetStyle = "true";
  document.head.appendChild(link);
}

function loadCalendlyWidget() {
  if (window.Calendly?.initPopupWidget) {
    return Promise.resolve(window.Calendly);
  }

  if (calendlyWidgetPromise) {
    return calendlyWidgetPromise;
  }

  ensureCalendlyWidgetStyles();

  calendlyWidgetPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(
      'script[data-calendly-widget-script="true"]'
    );
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.src = CALENDLY_WIDGET_SCRIPT_URL;
    script.async = true;
    script.dataset.calendlyWidgetScript = "true";
    script.onload = () => resolve(window.Calendly);
    script.onerror = () => {
      script.remove();
      calendlyWidgetPromise = null;
      reject(new Error("Failed to load Calendly widget script"));
    };
    document.head.appendChild(script);
  });

  return calendlyWidgetPromise;
}

document.addEventListener("DOMContentLoaded", () => {
  const sections = document.querySelectorAll(".shopify-section");

  function isDarkColor(rgb) {
    const values = rgb.match(/\d+/g);
    if (!values) return false;
  
    const r = parseInt(values[0], 10);
    const g = parseInt(values[1], 10);
    const b = parseInt(values[2], 10);
  
    // 感知亮度算法（推荐）
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
    return brightness < 128; // 小于128认为是深色
  }

  function getGradientFirstColor(bgImage) {
    const match = bgImage.match(/rgb[a]?\([^)]+\)/);
    return match ? match[0] : null;
  }
  
  // 添加深色背景检测
  sections.forEach(section => {
    const style = getComputedStyle(section);
    let bgColor = style.backgroundColor;
    const bgImage = style.backgroundImage;
  
    // ✅ 如果是渐变
    if (bgImage && bgImage !== 'none') {
      const gradientColor = getGradientFirstColor(bgImage);
  
      if (gradientColor) {
        bgColor = gradientColor;
      }
    }
  
    // ✅ 透明处理
    if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
      section.classList.add('is-light');
      section.classList.remove('is-dark');
      return;
    }
  
    // ✅ 判断深浅
    if (isDarkColor(bgColor)) {
      section.classList.add('is-dark');
      section.classList.remove('is-light');
    } else {
      section.classList.add('is-light');
      section.classList.remove('is-dark');
    }
  });

  // Click yg-link-top-cross to close the drawer
  const navDrawer = document.getElementById('header-sidebar-menu');
  const closeButtons = navDrawer.querySelectorAll('.yg-link-top-cross');
  closeButtons.forEach((closeBtn) => {
    closeBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      if (navDrawer.hide) {
        navDrawer.hide();
      } else {
        navDrawer.removeAttribute('open');
      }
    });
  });

  // 懒加载vr iframe
  const vrIframes = document.querySelectorAll('.yg-vr-iframe-lazy');
  const iframeObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.src = entry.target.getAttribute('data-src');
      }
    });
  });
  vrIframes.forEach((vrIframe) => {
    iframeObserver.observe(vrIframe);
  });

  // 懒加载视频
  const videos = document.querySelectorAll('.yg-video-lazy');
  const videoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.src = entry.target.getAttribute('data-src');
      }
    });
  });
  videos.forEach((video) => {
    videoObserver.observe(video);
  });

  document.addEventListener("click", async (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest("[data-calendly-url]");
    if (!trigger) return;

    const url = trigger.getAttribute("data-calendly-url");
    if (!url) return;

    event.preventDefault();

    try {
      await loadCalendlyWidget();

      if (window.Calendly?.initPopupWidget) {
        window.Calendly.initPopupWidget({ url });
        return;
      }
    } catch (error) {
      console.warn("Calendly widget lazy load failed", error);
    }

    const popup = window.open(url, "_blank", "noopener");
    if (!popup) {
      window.location.assign(url);
    }
  });
});
